/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import logoBw from "../assets/logo-bw.png";
import { notification, Input } from "antd";
import {
    Briefcase,
    Users,
    Scale,
    Vote,
    GraduationCap,
    FileSpreadsheet,
    Search as SearchIcon,
    Bell,
} from "lucide-react";
import { useSelectedUser } from "./staff/SelectedUserProvider";
import { getPatientRoaming } from "./service/patient.service";
import { getDashboardPathByRole } from "../utils/dashboardRoute";

const { Search } = Input;

const APPS = [
    { id: "hms", name: "HMS", icon: Briefcase, desc: "Hospital Management", active: true },
    { id: "hrms", name: "HRMS", icon: Users, desc: "Human Resources" },
    { id: "lsms", name: "LSMS", icon: Scale, desc: "Legal Services" },
    { id: "evote", name: "eVote", icon: Vote, desc: "Electronic Voting" },
    { id: "ssms", name: "SSMS", icon: GraduationCap, desc: "School Management" },
    { id: "sotpro", name: "SotPro", icon: FileSpreadsheet, desc: "Survey of Products" },
];

const NineDotsIcon = () => (
    <span className="hms-nine-dots" aria-hidden="true">
        {Array.from({ length: 9 }, (_, index) => (
            <span key={index} className="hms-nine-dots-dot" />
        ))}
    </span>
);

const initialsFrom = (name = '') =>
    String(name)
        .split(/[\s._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('') || 'U';

const Header = () => {
    const {
        setSelectedPatient,
        setSelectedVisit,
        loginUser,
        loginRole,
        handleLogoutUser,
        isLoggingOut,
        loginBranch,
        hospital,
    } = useSelectedUser();
    const dashboardPath = getDashboardPathByRole(loginRole);
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleSearchSubmit = () => {
        if (!searchTerm.trim()) {
            notification.error({
                message: "Error",
                description: "Search term cannot be empty",
            });
            return;
        }

        getPatientRoaming(searchTerm)
            .then((response) => {
                const patient = response.data;

                if (patient) {
                    sessionStorage.setItem("selectedPatient", JSON.stringify(patient));
                    setSelectedPatient(patient);
                    sessionStorage.removeItem("selectedVisit");
                    setSelectedVisit(null);
                    navigate("/patient-dashboard");
                } else {
                    notification.warning({
                        message: `Patient with this card number: ${searchTerm} does not exist`,
                    });
                }
            })
            .catch(() => {
                notification.error({ message: "Search failed. Please try again." });
            });
    };

    return (
        <header className="hms-topbar">
            <div className="hms-topbar-inner">
                <div className="hms-topbar-left">
                    <div className="dropdown hms-topbar-apps">
                        <button
                            type="button"
                            className="hms-topbar-apps-btn dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            aria-label="Apps"
                        >
                            <NineDotsIcon />
                        </button>
                        <div className="dropdown-menu hms-topbar-apps-menu">
                            <div className="hms-topbar-apps-label">Apps</div>
                            <div className="hms-topbar-apps-grid">
                                {APPS.map((app) => {
                                    const Icon = app.icon;
                                    return (
                                        <button
                                            key={app.id}
                                            type="button"
                                            className={`hms-topbar-app-tile${app.active ? " is-active" : ""}`}
                                            title={app.desc}
                                        >
                                            <span className="hms-topbar-app-icon">
                                                <Icon size={20} />
                                            </span>
                                            <span className="hms-topbar-app-name">{app.name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <Link to={dashboardPath} className="hms-topbar-brand">
                        <span className="hms-topbar-brand-logo">
                            <img src={logoBw} alt="" />
                        </span>
                        <span className="hms-topbar-brand-text">
                            <span className="hms-topbar-brand-name">Synergy Dynamics</span>
                            <span className="hms-topbar-brand-sep" aria-hidden="true">|</span>
                            <span className="hms-topbar-brand-sub">HMS</span>
                        </span>
                    </Link>
                </div>

                <div className="hms-topbar-search">
                    <Search
                        placeholder="Tell me what you want to do..."
                        allowClear
                        enterButton="Search"
                        size="small"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onSearch={handleSearchSubmit}
                        prefix={<SearchIcon size={14} className="hms-topbar-search-icon" />}
                    />
                </div>

                <div className="hms-topbar-right">
                    <div className="hms-topbar-branch d-none d-md-flex">
                        <span className="hms-topbar-branch-name">
                            {loginBranch?.name || "Branch"}
                        </span>
                    </div>

                    <Link to={dashboardPath} className="hms-topbar-bell" aria-label="Notifications">
                        <Bell size={18} />
                    </Link>

                    <ul className="nav hms-topbar-user">
                        <li className="nav-item dropdown has-arrow">
                            <Link
                                to="#"
                                className="dropdown-toggle nav-link hms-topbar-user-link"
                                data-bs-toggle="dropdown"
                            >
                                <div className="hms-topbar-user-meta d-none d-sm-block">
                                    <span className="hms-topbar-user-name">
                                        {loginUser?.firstName} {loginUser?.lastName}
                                    </span>
                                    <span className="hms-topbar-user-role">
                                        {loginRole?.roleName}
                                    </span>
                                </div>
                                <span className="hms-topbar-avatar hms-topbar-avatar--initials">
                                    {initialsFrom(`${loginUser?.firstName} ${loginUser?.lastName}`)}
                                </span>
                            </Link>
                            <div className="dropdown-menu dropdown-menu-end hms-topbar-user-menu">
                                <Link className="dropdown-item" to="/profile">My Profile</Link>
                                <Link className="dropdown-item" to="/settings">Settings</Link>
                                <Link
                                    className={`dropdown-item${isLoggingOut ? ' disabled' : ''}`}
                                    to="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (!isLoggingOut) handleLogoutUser();
                                    }}
                                    style={isLoggingOut ? { pointerEvents: 'none', opacity: 0.7 } : undefined}
                                >
                                    {isLoggingOut ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                            Signing out...
                                        </>
                                    ) : 'Logout'}
                                </Link>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </header>
    );
};

export default Header;
